import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type BookmakerConfig } from '@cx-web/api-clients/site-config-api/types';
import { type RepubIs } from '@cx-web/utils';
import { trackEvent } from '@cx-web/utils/dataLayer';
import { DomPurifySanitize } from '@cx-web/utils/us-utils';
import { type BetResult } from '@fpsm/cxy-types';
import clsx from 'clsx';

import { type Bet as TipsterBet } from '~/api/tipsters/types';
import { BetButton } from '~/components/bet-button/BetButton.tsx';
import { type Bet } from '~/components/BettingInsights/types';
import { GridTable, Profile, Status, TableGroup, useToggle } from '~/components/cxy/cxy.ts';

import { trackTipstersOddsClick } from '../../../tracking';

import styles from './styles/Tip.module.css';

export type TipsProps = {
  bet: TipsterBet;
  betDescription: string;
  betName: string;
  bookmaker?: BookmakerConfig;
  napOfTheDay?: boolean;
  repubIs: RepubIs;
  sport: string;
  stats: {
    odds: string | number;
    return: number;
    stake: number;
  };
  status?: BetResult;
  tip?: string;
  tipImage: string;
  tipName: string;
  tipster: string;
};

const Tip = ({
  bet,
  betDescription,
  betName,
  bookmaker,
  napOfTheDay = false,
  repubIs,
  sport,
  stats,
  status,
  tip,
  tipImage,
  tipName,
  tipster,
}: TipsProps) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const toggleTip = () => {
    if (!isOpen) {
      trackEvent('premium_tipster_tip_expanded', {
        eventLabel: 'Premium Tipster Tip Expanded',
      });
    }

    setIsOpen(!isOpen);
  };

  const TipSection = () => {
    const { Indicator, headingProps, sectionProps, triggerClass } = useToggle({
      isOpen,
      onChange: toggleTip,
    });

    return (
      <GridTable.Footer className={styles.tipFooterWrapper}>
        <GridTable.Row className={styles.tipFooter}>
          <GridTable.Cell {...sectionProps}>
            <div
              className={styles.tipFooterContent}
              dangerouslySetInnerHTML={{ __html: DomPurifySanitize(tip || '') }}
              data-testid="tip-info"
            />
          </GridTable.Cell>
          <GridTable.Cell {...headingProps} className={clsx(triggerClass, styles.trigger)} justifyContent="center">
            {isOpen ? t('CX_SHOW_LESS') : t('CX_SHOW_MORE')} <Indicator />
          </GridTable.Cell>
        </GridTable.Row>
      </GridTable.Footer>
    );
  };

  const betDetails: Bet = {
    betId: bet.id,
    betName: bet.name,
    bookmakerCode: bet.odds?.bookmakerCode as string,
    oddsAmerican: bet.odds?.american as string,
    oddsDecimal: bet.odds?.decimal as number,
    oddsFractional: bet.odds?.fractional as string,
    parentBetId: bet.id,
  };

  return (
    <TableGroup className={styles.tipsWrapper}>
      <GridTable className={styles.tips} label="Tips" mobileColumns={3}>
        <GridTable.Caption className={styles.caption}>
          {tipName}
          {status && <Status className={styles.status} status={status} />}
        </GridTable.Caption>
        <GridTable.Head className={styles.tableHeadings} row={3}>
          <GridTable.Row className={styles.tableRow}>
            <GridTable.Column justifyContent="center" showDividers>
              {bookmaker ? t('BEST_ODDS') : t('CX_PLUS_ODDS')}
            </GridTable.Column>
            <GridTable.Column justifyContent="center" showDividers>
              {t('STAKE')}
            </GridTable.Column>
            <GridTable.Column justifyContent="center" showDividers>
              {t('CX_PLUS_TIPSTERS_RETURN')}
            </GridTable.Column>
            {status && (
              <GridTable.Column className={styles.status} justifyContent="center">
                <Status status={status} />
              </GridTable.Column>
            )}
          </GridTable.Row>
        </GridTable.Head>
        <GridTable.Body>
          <GridTable.Row style={{ height: '100%' }}>
            <GridTable.Cell style={{ gridColumn: '1 / -1' }}>
              <Profile description={betDescription} image={tipImage} name={betName} />
            </GridTable.Cell>
          </GridTable.Row>
        </GridTable.Body>
        <GridTable.Body className={styles.test}>
          <GridTable.Row>
            <GridTable.Cell justifyContent="center" showDividers>
              {bookmaker ? (
                <BetButton
                  bet={betDetails}
                  bookieColour={bookmaker.brandColour || 'black'}
                  bookieLogo={bookmaker.logoSmall}
                  bookieName={bookmaker.bookmakerName}
                  hideBookieLogo={repubIs.GZ}
                  onClick={() => {
                    trackTipstersOddsClick({
                      tipster,
                      betName: bet.name,
                      market: bet.marketName,
                      sport,
                      bestOdds: stats.odds,
                      stake: stats.stake,
                      returnValue: stats.return,
                      napOfTheDay,
                    });
                  }}
                  shouldHideLabelOnDesktop
                  shouldHideLabelOnMobile
                  unformattedOdds={betDetails}
                />
              ) : (
                stats.odds
              )}
            </GridTable.Cell>
            <GridTable.Cell justifyContent="center" showDividers>
              {stats.stake} Pts
            </GridTable.Cell>
            <GridTable.Cell justifyContent="center" showDividers>
              {stats.return} Pts
            </GridTable.Cell>
            {status && (
              <GridTable.Cell className={styles.status} justifyContent="center">
                <p className={clsx(styles.statusColumn)}>{status}</p>
              </GridTable.Cell>
            )}
          </GridTable.Row>
        </GridTable.Body>
        {tip && <TipSection />}
      </GridTable>
    </TableGroup>
  );
};

export { Tip };
